import React, { useCallback, useEffect, useRef, useState } from 'react'
import { destroy } from 'redux-form'
import { View } from 'react-native'
import Typography from 'APP/Converse/Typography'
import { useNavigation } from '@react-navigation/native'
import { connect } from 'react-redux'
import Spinner from 'APP/Components/Spinner'
import { useDispatch } from 'APP/Hooks/redux'
import moment from 'moment'
import { Format } from 'APP/Lib/ProfileDataFormat'
import { getNameDisplay } from 'APP/Lib/ProfileHelpers'
import type { AppDispatch, RootState } from 'APP/Store/CreateStore'
import type { FamilyMember } from 'APP/Store/Family/types'

// Services
import I18n from 'APP/I18n'

// Actions
import { familyActions } from 'APP/Store/Family'

// Components
import { PrimaryButton } from 'APP/Components/Buttons'
import List from 'APP/Components/List'
import AddFamilyMemberModal from 'APP/Components/AddFamilyMemberModal'
import ListItem from 'APP/Converse/ListItem'
import NoticeBarContainer from 'APP/Components/NoticeBarContainer'
import DialogueBranding from 'APP/Components/DialogueBranding'
import Alert from 'APP/Converse/Alert'

// Styles
import { ApplicationStyles, Images } from 'APP/Themes'
import styled, { useTheme } from 'styled-components/native'
import { ResponsiveScrollView, ResponsiveView } from 'APP/Converse/Layout'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { isWeb } from 'APP/Helpers/checkPlatform'
import { selectIsAppOnline } from 'APP/Sagas/AppSessionSagas'

const Container = styled(ResponsiveView)`
  flex: 1;
`

const BottomSection = styled(ResponsiveView)<{ bottomInset: number }>`
  padding-bottom: ${({ bottomInset }) => bottomInset}px;
`

const TopSection = styled(View)`
  justify-content: center;
  align-items: center;
  max-height: 300;
  margin-vertical: ${({ theme }) => theme.metrics.baseMargin};
`

const SectionTitle = styled(Typography)`
  color: ${({ theme }) => theme.colors.text};
  text-align: left;
  margin-top: ${({ theme }) => theme.metrics.baseMargin};
  margin-left: ${({ theme }) => theme.metrics.baseMargin};
  padding-bottom: 10;
`

const SectionText = styled(Typography)`
  margin-vertical: ${({ theme }) => theme.metrics.baseMargin};
  margin-horizontal: ${({ theme }) => theme.metrics.baseMargin};
  text-align: center;
`

const ListLineStyle = {
  ...ApplicationStyles.listLine,
  marginTop: 0,
  paddingTop: 0,
}

type FamilyScreenProps = {
  familyMembers: FamilyMember[]
  invitedFamilyMembers: FamilyMember[]
  cancelAdultInviteRunning: boolean
  isConnected: boolean | null
  destroyForm: () => void
  cancelAdultInvite: (memberId: FamilyMember['id'], email: string) => void
}

function ScreenWithoutFamily({
  isConnected,
  destroyForm,
}: Pick<FamilyScreenProps, 'isConnected' | 'destroyForm'>) {
  const theme = useTheme()
  const insets = useSafeAreaInsets()
  const [isAddFamilyMemberVisible, setIsAddFamilyMemberVisible] = useState(false)

  const handleCloseAddFamilyMember = () => {
    destroyForm()
    setIsAddFamilyMemberVisible(false)
  }

  const handleMemberAdded = () => {
    handleCloseAddFamilyMember()
  }

  return (
    <Container testID="NO_FAMILY_VIEW" style={{ justifyContent: 'space-between' }}>
      <TopSection>
        <Images.emptyFamilyIcon
          // @ts-expect-error: emptyFamilyIcon SVG accepts custom fillLine, not in SvgProps
          fillLine={theme.colors.text}
          testID="EMPTY_FAMILY_ICON"
        />
        <SectionText variant="h3">{I18n.t('FamilyScreen.empty.body')}</SectionText>
      </TopSection>
      <BottomSection bottomInset={insets.bottom}>
        <PrimaryButton
          title={I18n.t('FamilyScreen.empty.buttonM')}
          analyticsName="FamilyScreen.empty.inviteFamilyMember"
          onPress={() => setIsAddFamilyMemberVisible(true)}
          disabled={!isConnected}
          testID="ADD_FAMILY_MEMBER_BTN"
        />
      </BottomSection>
      <AddFamilyMemberModal
        isVisible={isAddFamilyMemberVisible}
        onClose={handleCloseAddFamilyMember}
        onAdultAdded={handleMemberAdded}
        onChildAdded={handleMemberAdded}
      />
    </Container>
  )
}

function Family({ familyMembers }: Pick<FamilyScreenProps, 'familyMembers'>) {
  const navigation = useNavigation()
  if (!familyMembers || familyMembers.length === 0) return null
  return (
    <View>
      <SectionTitle variant="bodySmallBold">
        {I18n.t('FamilyScreen.full.familyMembers')}
      </SectionTitle>
      <List containerStyle={ListLineStyle}>
        {familyMembers.map((member) => {
          const isChild = member.type === 'child'
          const { nameDisplay } = getNameDisplay(
            member.givenName,
            member.preferredName,
            undefined,
            member.familyName
          )
          const title =
            nameDisplay || (isChild ? member.email : I18n.t('FamilyScreen.full.pendingInvite'))
          return (
            <ListItem
              key={member.id}
              title={title}
              subtitle={
                isChild ? moment(member.birthdate).format(Format.DISPLAY_DATE) : member.email
              }
              hideIconComponent={!isChild}
              onPress={
                isChild
                  ? () => {
                      navigation.navigate('dependentProfile', { childId: member.id })
                    }
                  : undefined
              }
              testID={isChild ? 'CHILD_FAMILY_MEMBER' : 'REGISTERED_FAMILY_MEMBER'}
            />
          )
        })}
      </List>
    </View>
  )
}

function InvitedFamily({
  invitedFamilyMembers,
  cancelAdultInvite,
}: Pick<FamilyScreenProps, 'invitedFamilyMembers' | 'cancelAdultInvite'>) {
  if (!invitedFamilyMembers || invitedFamilyMembers.length === 0) return null

  const handleDeleteInvite = (member: FamilyMember) => {
    return () =>
      Alert.alert(
        I18n.t('FamilyScreen.deleteInvite.title'),
        I18n.t('FamilyScreen.deleteInvite.body') + member.email + '?',
        [
          { text: I18n.t('FamilyScreen.deleteInvite.cancel') },
          {
            text: I18n.t('FamilyScreen.deleteInvite.delete'),
            // @ts-expect-error FIXME: adults always have their email set, but MA code doesn't narrow the type
            onPress: () => cancelAdultInvite(member.id, member.email),
          },
        ]
      )
  }

  return (
    <View>
      <SectionTitle variant="bodySmallBold">{I18n.t('FamilyScreen.full.invited')}</SectionTitle>
      <List containerStyle={ApplicationStyles.screen.listContainer}>
        {invitedFamilyMembers.map((member) => {
          return (
            <ListItem
              key={member.email}
              title={I18n.t('FamilyScreen.full.pendingInvite')}
              subtitle={member.email}
              iconName={member.permissions === 'delete' ? 'close' : 'chevron-right'}
              onPress={handleDeleteInvite(member)}
              testID={`INVITED_FAMILY_MEMBER_${member.id}`}
            />
          )
        })}
      </List>
    </View>
  )
}

function ScreenWithFamily({
  cancelAdultInvite,
  cancelAdultInviteRunning,
  familyMembers,
  invitedFamilyMembers,
  isConnected,
  destroyForm,
}: FamilyScreenProps) {
  const insets = useSafeAreaInsets()
  const [isAddFamilyMemberVisible, setIsAddFamilyMemberVisible] = useState(false)

  const handleCloseAddFamilyMember = () => {
    destroyForm()
    setIsAddFamilyMemberVisible(false)
  }

  const handleMemberAdded = () => {
    handleCloseAddFamilyMember()
  }
  return (
    <Container testID={'WITH_FAMILY_VIEW'}>
      <Spinner visible={cancelAdultInviteRunning} cancelable={false} />
      <ResponsiveScrollView>
        <Family familyMembers={familyMembers} />
        <InvitedFamily
          invitedFamilyMembers={invitedFamilyMembers}
          cancelAdultInvite={cancelAdultInvite}
        />
      </ResponsiveScrollView>
      <BottomSection bottomInset={insets.bottom}>
        <PrimaryButton
          title={I18n.t('FamilyScreen.full.buttonM')}
          analyticsName="FamilyScreen.full.inviteFamilyMember"
          onPress={() => setIsAddFamilyMemberVisible(true)}
          disabled={!isConnected}
          testID="ADD_FAMILY_MEMBER_BTN"
        />
      </BottomSection>
      <AddFamilyMemberModal
        isVisible={isAddFamilyMemberVisible}
        onClose={handleCloseAddFamilyMember}
        onAdultAdded={handleMemberAdded}
        onChildAdded={handleMemberAdded}
      />
    </Container>
  )
}

function FamilyScreen(props: FamilyScreenProps) {
  const screenReaderTextRef = useRef<{ focus: () => void } | null>(null)
  const dispatch = useDispatch()
  const getFamily = useCallback(() => dispatch(familyActions.fetchFamilyRequest()), [dispatch])

  const { familyMembers, invitedFamilyMembers } = props

  useEffect(() => {
    if (screenReaderTextRef.current && isWeb()) {
      screenReaderTextRef.current.focus()
    }
  }, [])

  useEffect(() => {
    getFamily()
  }, [getFamily])

  return (
    <NoticeBarContainer>
      {isWeb() && (
        // @ts-expect-error: Typography is not forwardRef; web uses ref+tabIndex for screen reader focus
        <Typography variant="screenReaderOnly" ref={screenReaderTextRef} tabIndex={-1}>
          {I18n.t('FamilyScreen.title')}
        </Typography>
      )}
      {familyMembers.length > 0 || invitedFamilyMembers.length > 0 ? (
        <ScreenWithFamily {...props} />
      ) : (
        <ScreenWithoutFamily {...props} />
      )}
      <DialogueBranding />
    </NoticeBarContainer>
  )
}

const mapStateToProps = (state: RootState, props: { route?: { params?: object } }) => {
  const family = state.family
  const memberArray = Object.values(family.members)

  const getInvitedMembers = (members: FamilyMember[]) =>
    members.filter((m) => m?.status !== 'accepted' && m?.type === 'guardian')

  const getRegisteredMembers = (selfId: string | number | undefined, members: FamilyMember[]) => {
    return members.filter(
      (m) => (m?.status === 'accepted' && selfId !== m.id) || m?.type === 'child'
    )
  }

  return {
    ...props.route?.params,
    familyMembers: getRegisteredMembers(state.patient.profile.id, memberArray),
    invitedFamilyMembers: getInvitedMembers(memberArray),
    cancelAdultInviteRunning: family.requestRunning,
    isConnected: selectIsAppOnline(state),
  }
}

const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    destroyForm: () => dispatch(destroy('updateDependentForm')),
    cancelAdultInvite: (memberId: FamilyMember['id'], email: string) =>
      dispatch(familyActions.cancelAdultInviteRequest({ memberId, email })),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(FamilyScreen)
